import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { Grid } from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import useFetch from 'hooks/useFetch';

function ModalInfo({ show, handleClose, documentId }) {
  const [documentData, setDocumentData] = useState(null);

  // Importante: usar la ruta correcta y pasar dependencias
  const { data, isLoading, error } = useFetch(
    `/api/finance/invoice/${documentId}/`,
    { method: 'GET' },
    [show, documentId]
  );

  useEffect(() => {
    if (show) setDocumentData(data);
  }, [show, data]);

  if (!show) return null; // Solo control por visibilidad

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      aria-labelledby="modal-info-title"
      centered
      className="modal-container"
      backdropClassName="modal-backdrop"
      backdrop="static"
      style={{ zIndex: 1050 }}
    >
      <Modal.Header closeButton>
        <Modal.Title id="modal-info-title">Detalles del Documento</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading && (
          <SoftTypography variant="body2">Cargando información…</SoftTypography>
        )}

        {error && (
          <SoftTypography variant="body2" color="error">
            Error: {error}
          </SoftTypography>
        )}

        {!isLoading && !error && documentData && (
          <SoftBox mt={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <SoftBox display="flex" flexDirection="column" gap={2}>
                  <InfoField label="Cliente" value={documentData.cliente} />
                  <InfoField label="Ciudad" value={documentData.ciudad} />
                  <InfoField label="Comuna" value={documentData.comuna} />
                  <InfoField label="Dirección" value={documentData.direccion} />
                  <InfoField label="Condiciones" value={documentData.condiciones} />
                </SoftBox>
              </Grid>

              <Grid item xs={12} md={4}>
                <SoftBox display="flex" flexDirection="column" gap={2}>
                  <InfoField label="R.U.T." value={documentData.rut} />
                  <InfoField label="Teléfono" value={documentData.telefono} />
                  <InfoField label="N° Orden" value={documentData.numeroOrden} />
                  <InfoField label="Operación" value={documentData.operacion} />
                  <InfoField label="Flete" value={documentData.flete || 'No especificado'} />
                </SoftBox>
              </Grid>

              <Grid item xs={12} md={4}>
                <SoftBox display="flex" flexDirection="column" gap={2}>
                  <InfoField label="Razón Social" value={documentData.razonSocial} />
                  <InfoField
                    label="Tipo Cambio"
                    value={
                      typeof documentData.tipoCambio === 'number'
                        ? documentData.tipoCambio.toLocaleString('es-CL')
                        : documentData.tipoCambio
                    }
                  />
                  <InfoField label="BL/Guía Aérea" value={documentData.blGuiaAerea || 'No especificado'} />
                  <InfoField label="Tipo Moneda" value={documentData.tipoMoneda || 'No especificado'} />
                  <InfoField
                    label="Costo"
                    value={
                      typeof documentData.costo === 'number'
                        ? documentData.costo.toLocaleString('es-CL')
                        : documentData.costo
                    }
                  />
                </SoftBox>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <InfoField label="Nota 1" value={documentData.nota1} />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoField label="Nota 2" value={documentData.nota2} />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoField label="Nota 3" value={documentData.nota3} />
              </Grid>
            </Grid>
          </SoftBox>
        )}
      </Modal.Body>

      <Modal.Footer>
        <SoftButton variant="outlined" color="dark" onClick={handleClose}>
          Cerrar
        </SoftButton>
      </Modal.Footer>
    </Modal>
  );
}

// Helper para mostrar campos
const InfoField = ({ label, value }) => (
  <SoftBox>
    <SoftTypography variant="caption" fontWeight="bold" color="text">
      {label}
    </SoftTypography>
    <SoftTypography variant="body2" color="text">
      {value ?? 'No especificado'}
    </SoftTypography>
  </SoftBox>
);

InfoField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ModalInfo.propTypes = {
  show: PropTypes.bool,
  handleClose: PropTypes.func,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ModalInfo.defaultProps = {
  show: false,
};

export default ModalInfo;
